<script setup>
// Page 4 — how a month runs: one-off consent, the agreed rhythm, and the only part
// that still asks for a human. A real sequence, so the numbering carries meaning: a
// spark jumps the gap from card to card, landing each icon chip in its "arrived"
// state (the same secondary → primary language HeroFlow/FlowRail use for origin →
// destination). The third step is the one that never finishes — it is what the board
// keeps doing every month — so once it lands it's handed off into the site's
// mechanical ticking-ring loader (.spinner) instead of settling still like the others.
//
// This is where the hero's primary CTA points, which is why the steps describe the
// running month rather than the one-time setup: someone who clicked "bekijk hoe
// Pandig dit voorkomt" is asking what happens after it's set up, not what installing
// it costs them. The setup sequence lives in the closing CTA instead.
//
// Scroll-scrubbed over a taller sticky section (the same shape as LandingStakes and
// FeatureDeck), not a once-triggered animation on a single-viewport .snap-page:
// landing on a snap point doesn't stop the next scroll gesture from immediately
// moving on, so the old version could be scrolled straight past before the
// sequence finished playing. Tying every beat to how far you've scrolled means
// it can't be outrun, and it reverses cleanly — scrub back up and the sparks
// retreat, the chips un-arrive, the ring stops — instead of leaving stray state
// behind.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { Link2, Users, Zap } from "@lucide/vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, collapseRail, SCRUB, DIM } from "@/lib/motion";

const { t } = useI18n();

const steps = [
  { n: "landing.how.step1_n", title: "landing.how.step1_title", body: "landing.how.step1_body", icon: Link2 },
  { n: "landing.how.step2_n", title: "landing.how.step2_title", body: "landing.how.step2_body", icon: Users },
  { n: "landing.how.step3_n", title: "landing.how.step3_title", body: "landing.how.step3_body", icon: Zap },
];

const root = ref(null);
const stepEls = ref([]);
const iconEls = ref([]);
const connLines = ref([]);
const connDots = ref([]);

// Which icon chips have "arrived" (secondary → primary), and whether the third
// one has been handed off to its ticking ring — recomputed from the timeline's
// own progress on every scroll update (not one-shot callbacks), so scrubbing
// back up un-arrives them just as cleanly as scrubbing down arrived them.
const activated = ref([false, false, false]);
const spinning = ref(false);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) {
      gsap.set(stepEls.value, { opacity: 1, y: 0, scale: 1 });
      activated.value = [false, true, true];
      return;
    }

    // DIM, not 0: the three steps are the mechanism the hero's primary CTA promised
    // to show. Landing here from that CTA (or from /#hoe-het-werkt) has to land on
    // readable copy, so what the scrub adds is the sequencing, not the existence.
    gsap.set(stepEls.value, { opacity: DIM, y: 26, scale: 0.94 });
    gsap.set(connDots.value, { opacity: 0 });
    const railTips = connLines.value.map((line) => collapseRail(line));

    // Built paused, then handed to ScrollTrigger below as `animation` — it must
    // not autoplay on its own authored durations, only ever advance via scrub.
    const tl = gsap.timeline({ paused: true });
    const arrivesAt = [0, 0, 0];

    stepEls.value.forEach((el, i) => {
      tl.to(el, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.7)" }, i === 0 ? 0 : "-=0.32");
    });

    // The connector rail between cards is `hidden sm:flex` — invisible on any real
    // phone — so on narrow viewports skip wiring its tweens entirely rather than
    // paying for scrub-driven stroke/opacity/position updates nothing ever shows.
    const hasConnectors = window.matchMedia("(min-width: 640px)").matches;

    // Each spark jumps from card i to card i+1: the rail draws, a pulse rides it
    // in hard steps (the same un-smooth travel as HeroFlow/FlowRail), and the
    // instant it lands the next icon chip flips to "arrived" with a small pop.
    [0, 1].forEach((i) => {
      const label = `spark${i}`;
      tl.addLabel(label, i === 0 ? "+=0.1" : "+=0.15");
      if (hasConnectors) {
        tl.to(connLines.value[i], { attr: { x2: railTips[i] }, duration: 0.2, ease: "power1.inOut" }, label)
          .to(connDots.value[i], { opacity: 1, duration: 0.04 }, label)
          .to(connDots.value[i], { left: "100%", duration: 0.2, ease: "steps(6)" }, label);
      }

      arrivesAt[i + 1] = tl.labels[label] + 0.2;

      tl.fromTo(iconEls.value[i + 1], { scale: 1 }, { scale: 1.22, duration: 0.16, ease: "back.out(2.6)" }, `${label}+=0.2`)
        .to(iconEls.value[i + 1], { scale: 1, duration: 0.22 });
      if (hasConnectors) tl.to(connDots.value[i], { opacity: 0, duration: 0.12 });
    });

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
      // Only write the refs when a value actually flips — Vue writes the class
      // attribute unconditionally on every ref assignment, so doing this on every
      // scrub tick forced a style recalc on the icon chips for the entire scroll
      // range, not just the two moments the state changes.
      onUpdate: (self) => {
        const time = self.progress * tl.duration();
        const next = [false, time >= arrivesAt[1], time >= arrivesAt[2]];
        if (next[1] !== activated.value[1] || next[2] !== activated.value[2]) activated.value = next;
        const spin = self.progress >= 1;
        if (spin !== spinning.value) spinning.value = spin;
      },
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Its content fades in across the section's own scroll range, so jumping to
       the section's top edge would land on a blank panel: a menu/pager jump goes
       to the settled end instead (see scrollElementIntoView in lib/scroll.js). -->
  <section
    id="hoe-het-werkt"
    ref="root"
    class="relative border-t-2 border-foreground bg-card"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[130vh] sm:h-[160vh]'"
    data-jump="settled"
    :data-page-label="t('landing.pager.how')"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-5 sm:py-14">
        <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
          {{ t("landing.how.title") }}
        </h2>

        <ol class="mt-4 grid gap-3 sm:mt-9 sm:grid-cols-3 sm:gap-8">
          <li
            v-for="(step, i) in steps"
            :key="step.n"
            :ref="(el) => (stepEls[i] = el)"
            class="step-item relative border-2 border-foreground bg-card p-3.5 shadow-[var(--shadow-hard)] sm:p-7"
          >
            <!-- Three cards side by side on a desktop, one under the other on a
                 phone — and stacked, the chip/number band above the text costs a
                 line of height per card that a phone can't spare. So on a phone the
                 band turns on its side and becomes a left rail instead, with the
                 title and body filling the room next to it. -->
            <div class="flex items-start gap-3 sm:block">
              <div class="flex shrink-0 items-center gap-2 sm:w-full sm:justify-between">
                <span class="relative flex size-9 items-center justify-center sm:size-11">
                  <span
                    v-if="i === 2 && spinning"
                    class="spinner absolute -inset-2 border-2 border-foreground/30 border-r-transparent"
                    aria-hidden="true"
                  />
                  <span
                    :ref="(el) => (iconEls[i] = el)"
                    class="relative flex size-9 items-center justify-center border-2 border-foreground transition-colors duration-200 sm:size-11"
                    :class="activated[i] ? 'bg-primary text-primary-foreground' : 'bg-secondary text-primary'"
                  >
                    <component :is="step.icon" class="size-4 sm:size-5" />
                  </span>
                </span>
                <span class="font-mono text-xl font-semibold tabular-nums text-primary sm:text-3xl">{{ t(step.n) }}</span>
              </div>
              <div class="min-w-0">
                <h3 class="font-sans text-base font-semibold tracking-tight text-foreground sm:mt-4 sm:text-lg">{{ t(step.title) }}</h3>
                <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-2 sm:text-sm sm:leading-relaxed">{{ t(step.body) }}</p>
              </div>
            </div>

            <div
              v-if="i < steps.length - 1"
              class="absolute -right-8 top-1/2 z-10 hidden h-4 w-8 -translate-y-1/2 items-center sm:flex"
              aria-hidden="true"
            >
              <svg class="h-2.5 w-full text-foreground/25" viewBox="0 0 100 20" preserveAspectRatio="none">
                <line
                  :ref="(el) => (connLines[i] = el)"
                  x1="4"
                  y1="10"
                  x2="90"
                  y2="10"
                  stroke="currentColor"
                  stroke-width="2"
                  vector-effect="non-scaling-stroke"
                />
              </svg>
              <span :ref="(el) => (connDots[i] = el)" class="absolute left-0 top-1/2 size-2 -translate-y-1/2 border border-foreground bg-primary opacity-0" />
            </div>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>
